import * as React from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/utils";
import { controlClasses } from "./variants";

/* ------------------------------------------------------------------ Input */

const inputSizes = {
  lg: { input: "h-12 px-4 text-sm", withIcon: "h-12 pl-11 pr-4 text-sm", icon: "left-4" },
  md: { input: "h-10 px-3 max-sm:h-11", withIcon: "h-10 pl-10 pr-3 max-sm:h-11", icon: "left-3" },
  sm: { input: "h-9 px-3", withIcon: "h-9 pl-9 pr-3", icon: "left-2.5" },
};

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Leading icon rendered inside the field */
  icon?: React.ReactNode;
  /** Trailing adornment (unit, button, etc.) */
  suffix?: React.ReactNode;
  /** Leading text adornment such as "$"; use instead of `icon` for units */
  prefix?: string;
  inputSize?: "lg" | "md" | "sm";
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, icon, suffix, prefix, inputSize = "md", invalid, ...props }, ref) => {
    const sizes = inputSizes[inputSize];
    const lead = icon ?? (prefix ? <span className="text-sm text-gray-500">{prefix}</span> : null);
    const field = (
      <input
        ref={ref}
        aria-invalid={invalid || props["aria-invalid"] || undefined}
        className={cn(
          controlClasses,
          "file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium",
          lead ? sizes.withIcon : sizes.input,
          prefix && !icon && "pl-7",
          suffix && "pr-12",
          className
        )}
        {...props}
      />
    );

    if (!lead && !suffix) return field;

    return (
      <div className="relative flex items-center">
        {lead && (
          <span
            className={cn(
              "pointer-events-none absolute flex items-center text-gray-400 [&_svg]:size-4",
              sizes.icon
            )}
          >
            {lead}
          </span>
        )}
        {field}
        {suffix && (
          <span className="absolute right-3 flex items-center text-sm text-gray-500">
            {suffix}
          </span>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

/* ---------------------------------------------------------- PasswordInput */

export type PasswordInputProps = Omit<InputProps, "type" | "suffix">;

/** Password field with an accessible show/hide toggle */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>((props, ref) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <Input
      ref={ref}
      {...props}
      type={visible ? "text" : "password"}
      suffix={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="-mr-1.5 flex size-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      }
    />
  );
});
PasswordInput.displayName = "PasswordInput";

/* ----------------------------------------------------------------- Select */

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  selectSize?: "md" | "sm";
  invalid?: boolean;
  /** Set false for an inline, content-width select (e.g. "Sort by") */
  fullWidth?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, selectSize = "md", invalid, fullWidth = true, ...props }, ref) => (
    <div className={cn("relative", fullWidth ? "w-full" : "inline-block w-auto")}>
      <select
        ref={ref}
        aria-invalid={invalid || props["aria-invalid"] || undefined}
        className={cn(
          controlClasses,
          "cursor-pointer appearance-none pl-3 pr-9",
          selectSize === "sm" ? "h-9" : "h-10 max-sm:h-11",
          className
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
    </div>
  )
);
Select.displayName = "Select";

/* --------------------------------------------------------------- TextArea */

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      aria-invalid={invalid || props["aria-invalid"] || undefined}
      className={cn(controlClasses, "min-h-[104px] resize-y px-3 py-2.5 leading-relaxed", className)}
      {...props}
    />
  )
);
TextArea.displayName = "TextArea";

/* ------------------------------------------------------------------ Label */

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, required, children, ...props }, ref) => (
    <label
      ref={ref}
      className={cn("mb-1.5 block text-sm font-medium text-gray-800", className)}
      {...props}
    >
      {children}
      {required && <span className="ml-0.5 text-gray-400">*</span>}
    </label>
  )
);
Label.displayName = "Label";

/* ------------------------------------------------------------------ Field */

export interface FieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** Inline action at the right of the label row, e.g. a "Forgot password?" link */
  labelAction?: React.ReactNode;
  children: React.ReactNode;
}

/** Label + control + hint/error, laid out consistently */
export const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  labelAction,
  children,
}) => (
  <div className={cn("space-y-1.5", className)}>
    {label && labelAction ? (
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={htmlFor} required={required} className="mb-0">
          {label}
        </Label>
        <div className="text-meta">{labelAction}</div>
      </div>
    ) : (
      label && (
        <Label htmlFor={htmlFor} required={required} className="mb-0">
          {label}
        </Label>
      )
    )}
    {children}
    {error ? (
      <p className="text-meta text-red-600">{error}</p>
    ) : hint ? (
      <p className="text-meta text-gray-500">{hint}</p>
    ) : null}
  </div>
);

/* --------------------------------------------------------------- Checkbox */

export type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement>;

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type="checkbox"
      className={cn(
        "size-4 shrink-0 cursor-pointer rounded border-gray-300 accent-brand-600",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        className
      )}
      {...props}
    />
  )
);
Checkbox.displayName = "Checkbox";

/* ------------------------------------------------------------ RadioButton */

export type RadioButtonProps = React.InputHTMLAttributes<HTMLInputElement>;

export const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type="radio"
      className={cn("size-4 shrink-0 cursor-pointer accent-brand-600", className)}
      {...props}
    />
  )
);
RadioButton.displayName = "RadioButton";

/* ----------------------------------------------------------------- Switch */

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "value"> {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name; required when the switch has no visible `<Label htmlFor>` */
  label?: string;
  loading?: boolean;
  size?: "md" | "sm";
}

/** On/off toggle (role="switch"). Use for settings that apply immediately. */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, label, loading, disabled, size = "md", className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        size === "sm" ? "h-5 w-8" : "h-6 w-10",
        checked ? "bg-gray-900" : "bg-gray-200 hover:bg-gray-300",
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-150",
          size === "sm" ? "size-4" : "size-5",
          checked ? (size === "sm" ? "translate-x-[14px]" : "translate-x-[18px]") : "translate-x-0.5"
        )}
      >
        {loading && (
          <span className="size-2.5 animate-spin rounded-full border-[1.5px] border-gray-300 border-t-gray-500" />
        )}
      </span>
    </button>
  )
);
Switch.displayName = "Switch";
